import Button from '@components/atoms/button'
import { FastlyImage } from '@components/common/image'
import { useProfileContext } from '@contexts/profile'
import { PROFILE_IMAGE_PATH } from '@lib/constants/profile'
import { ClassroomProjectType } from '@lib/types/profile'
import Link from 'next/link'
import React, { useMemo } from 'react'

type ProjectCardProps = {
  project: ClassroomProjectType
}

export const ProjectCard = React.memo(({ project }: ProjectCardProps) => (
  <div className="flex flex-col border border-grey-500 rounded-lg leading-tight">
    <div>
      <FastlyImage
        src={`${PROFILE_IMAGE_PATH}default-project-thumbnail.png`}
        layout="responsive"
        className="rounded-t-lg"
      />
    </div>
    <div className="flex flex-col p-5 h-full justify-between space-y-3">
      <div className="flex flex-col space-y-3">
        <h3 className="text-lg font-600">{project?.title || 'Project'}</h3>
        <p>{project?.outline}</p>
      </div>
      {project?.projectUrl && (
        <a
          href={project.projectUrl}
          className=""
          target="_blank"
          rel="noreferrer"
        >
          <Button>View project</Button>
        </a>
      )}
    </div>
  </div>
))

type Props = {
  projectType: 'projects' | 'assignments'
}

const Projects = ({ projectType }: Props) => {
  const { profileDetails } = useProfileContext()

  const projects: ClassroomProjectType[] = useMemo(
    () =>
      projectType === 'projects'
        ? profileDetails?.projects
        : profileDetails?.assignments,
    []
  )
  const viewMoreLink = useMemo(
    () => `/@${profileDetails?.username}/${projectType}`,
    []
  )
  if (!projects || projects.length === 0) return null
  return (
    <div className="flex flex-col p-5 lg:p-10 bg-white rounded-lg text-grey items-start">
      <h2 className="text-xl font-600 text-grey max-w-2xl mb-4">
        {projectType === 'projects' ? 'Activities' : 'Projects'}
      </h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 w-full mb-4">
        {projects.map((project) => (
          <ProjectCard key={project.submittedAt} project={project} />
        ))}
      </div>
      <Link href={viewMoreLink}>
        <a className="text-orange hover:underline font-600">View more</a>
      </Link>
    </div>
  )
}

export default React.memo(Projects)
